import type { Item, ItemPower } from '../content/types.ts';
import type { RuntimeState } from '../domain/runtime.ts';
import { inEncounter } from '../domain/runtime.ts';
import type { Hotlist } from './hotlist.ts';
import { hotlistAllocation } from './hotlist.ts';
import type { InventoryEntry } from './inventory.ts';
import { handAllocationField, inHandCopies, unallocatedCopies } from './inventory.ts';
import { encounterActionProblem } from './encounter.ts';
import { potionCadenceRemaining } from './itemUse.ts';
import { ROUND_SECONDS, describeElapsed } from './gameTime.ts';
import type { ExpirationCondition, TimedEffectRecord } from '../domain/encounter.ts';

/**
 * Item Powers: charges, recharge and cooldown, and whether a crawler can
 * activate one right now.
 *
 * PROJECT RULING - HOMEBREW (docs/RULES_CONFLICTS.md OPEN-018). See `ItemPower`.
 *
 * PURE. Nothing here writes, rolls or spends. The same functions run in the
 * browser (to draw the button honestly) and on the Sheets server (to decide
 * whether a use is allowed), so the two cannot disagree about what "2 charges
 * left" means.
 *
 * TIME IS THE ROOM'S ITEM CLOCK, a running count of in-game seconds the GM has
 * passed (rests, hours, combat rounds). State is stored as "what it was at
 * second N" and worked out for NOW on every read. Nothing has to visit every
 * Item each time a round ends, and an Item that sat in the stash for a week of
 * game time is exactly as recharged as one that was carried.
 */

/** What the room stores for one Item. Seconds are on the room's Item clock. */
export interface ItemPowerState {
  /** Charges as of `chargesAsOf`; null when the Power has no charges. */
  charges: number | null;
  /** When the recharge timer last started (or last paid out). */
  chargesAsOf: number;
  /** The clock second from which it may be activated again. */
  readyAt: number;
}

export interface ItemPowerStatus {
  /** Normalised state as of `clock`, safe to store. */
  state: ItemPowerState;
  charges: number | null;
  maxCharges: number | null;
  /** Seconds until the next recharge pays out; null when full or not recharging. */
  secondsToNextCharge: number | null;
  cooldownSecondsRemaining: number;
  /** Why it cannot be activated right now, or null. */
  notReady: string | null;
}

function wholeAtLeastZero(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

/** Reads stored state, or null when it is absent or not in a shape this version wrote. */
export function readItemPowerState(raw: unknown): ItemPowerState | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  const charges = record['charges'];
  if (charges !== null && !wholeAtLeastZero(charges)) return null;
  if (!wholeAtLeastZero(record['chargesAsOf']) || !wholeAtLeastZero(record['readyAt'])) return null;
  return { charges, chargesAsOf: record['chargesAsOf'], readyAt: record['readyAt'] };
}

/**
 * The Power's state at `clock`, given what was last stored.
 *
 * A missing record means "never used": full, and ready. Stored charges are
 * clamped to the current maximum, so a GM lowering the maximum takes effect
 * without a second edit.
 *
 * PARTIAL PROGRESS IS KEPT. Only whole recharge periods pay out, and the timer
 * moves forward by exactly the periods paid, so three 40-minute rests recharge
 * a two-hourly charge. A full Item does not bank time towards a charge it has
 * not spent: the timer restarts when the first charge is used.
 */
export function itemPowerStatus(power: ItemPower, stored: ItemPowerState | null, clock: number): ItemPowerStatus {
  const now = Math.max(0, Math.floor(clock));
  const readyAt = stored?.readyAt ?? 0;
  const cooldownSecondsRemaining = power.cooldownSeconds === null ? 0 : Math.max(0, readyAt - now);

  if (power.charges === null) {
    return {
      state: { charges: null, chargesAsOf: now, readyAt },
      charges: null,
      maxCharges: null,
      secondsToNextCharge: null,
      cooldownSecondsRemaining,
      notReady: cooldownSecondsRemaining > 0 ? `Ready again in ${describeElapsed(cooldownSecondsRemaining)}.` : null,
    };
  }

  const max = power.charges.max;
  let charges = Math.min(max, stored?.charges ?? max);
  let asOf = Math.min(now, stored?.chargesAsOf ?? now);
  let secondsToNextCharge: number | null = null;
  const recharge = power.charges.recharge;

  if (charges < max && recharge !== null) {
    const periods = Math.floor((now - asOf) / recharge.seconds);
    if (periods > 0) {
      charges = recharge.amount === 'all' ? max : Math.min(max, charges + periods * recharge.amount);
      asOf += periods * recharge.seconds;
    }
    if (charges < max) secondsToNextCharge = recharge.seconds - (now - asOf);
  }
  if (charges >= max) asOf = now;

  const notReady = charges < 1
    ? (secondsToNextCharge === null
      ? 'It has no charges left and does not recharge by itself.'
      : `No charges left. The next one returns in ${describeElapsed(secondsToNextCharge)}.`)
    : cooldownSecondsRemaining > 0
      ? `Ready again in ${describeElapsed(cooldownSecondsRemaining)}.`
      : null;

  return {
    state: { charges, chargesAsOf: asOf, readyAt },
    charges,
    maxCharges: max,
    secondsToNextCharge,
    cooldownSecondsRemaining,
    notReady,
  };
}

/** True when the Power keeps anything between uses. Such an Item must be one of a kind. */
export function itemPowerKeepsState(power: ItemPower): boolean {
  return power.charges !== null || power.cooldownSeconds !== null;
}

export type SpendItemPower =
  | { ok: true; state: ItemPowerState }
  | { ok: false; reason: string };

/** One activation: a charge is spent and the cooldown starts. */
export function spendItemPower(power: ItemPower, stored: ItemPowerState | null, clock: number): SpendItemPower {
  const status = itemPowerStatus(power, stored, clock);
  if (status.notReady !== null) return { ok: false, reason: status.notReady };
  const now = Math.max(0, Math.floor(clock));
  return {
    ok: true,
    state: {
      charges: status.charges === null ? null : status.charges - 1,
      // `itemPowerStatus` already set this to `now` for a full Item, which is
      // what starts the recharge timer on the first charge spent.
      chargesAsOf: status.state.chargesAsOf,
      readyAt: power.cooldownSeconds === null ? status.state.readyAt : now + power.cooldownSeconds,
    },
  };
}

/** A GM setting the charges by hand. The recharge timer restarts from now. */
export function setItemPowerCharges(power: ItemPower, stored: ItemPowerState | null, clock: number, charges: number): ItemPowerState {
  const status = itemPowerStatus(power, stored, clock);
  if (power.charges === null) return { ...status.state, readyAt: 0 };
  return {
    charges: Math.max(0, Math.min(power.charges.max, Math.floor(charges))),
    chargesAsOf: Math.max(0, Math.floor(clock)),
    readyAt: 0,
  };
}

/** Where the copy that gets used up comes from. */
export type ConsumedFrom = 'free' | 'hotlist' | 'held' | 'equipped';

export interface ItemPowerPlan {
  canUse: boolean;
  refusal: string | null;
  spendsAction: boolean;
  /** Null when nothing is used up. */
  consumedFrom: ConsumedFrom | null;
  /** RULE DCC-POT-004: a second Potion too soon still works, and poisons. */
  causesPotionSickness: boolean;
}

/**
 * Whether this crawler can activate this Item right now, apart from charges
 * and cooldown (which belong to the Item and are judged by `itemPowerStatus`).
 *
 * PRINTED RULES THAT STILL APPLY, because a homebrew Item is still an Item:
 *  - Rules p.19: reaching into the Inventory mid-fight is the Retrieve Action,
 *    which is not automated, so in a fight the Item must be worn, in a hand or
 *    in the Hotlist.
 *  - Rules p.49: drinking a Potion in combat costs an Action whatever the
 *    definition says, and a second within twenty seconds causes Potion
 *    Sickness (DCC-POT-002, DCC-POT-004).
 */
export function planItemPower(
  item: Item | undefined,
  character: { id: string; inventory: readonly InventoryEntry[]; hotlist: Hotlist },
  runtime: RuntimeState,
): ItemPowerPlan {
  const refuse = (refusal: string): ItemPowerPlan => ({
    canUse: false, refusal, spendsAction: false, consumedFrom: null, causesPotionSickness: false,
  });
  if (item === undefined) return refuse('This installation does not have that Item, so it cannot be used.');
  const power = item.power;
  if (power === undefined) return refuse(`${item.name} has nothing to activate.`);

  const entry = character.inventory.find((e) => e.itemId === item.id);
  if (entry === undefined || entry.quantity < 1) return refuse(`The crawler does not have ${item.name}.`);

  const fighting = inEncounter(runtime);
  const inHand = inHandCopies(entry, item);
  const worn = entry.equipped > 0 || entry.held > 0;
  const hotlisted = hotlistAllocation(character.hotlist as Hotlist, item.id);
  const free = unallocatedCopies(entry) - hotlisted;

  if (power.requiresEquipped && !worn) {
    return refuse(`${item.name} has to be ${item.slot === 'hands' ? 'in hand' : 'worn'} to use ${power.name}.`);
  }
  if (fighting && !worn && hotlisted < 1) {
    return refuse(
      `${item.name} is in the Inventory. Reaching into the Inventory mid-fight is the Retrieve Action ` +
        '(Rules p.19), which is not automated yet. Put it on the Hotlist, wear it or hold it first.',
    );
  }

  let consumedFrom: ConsumedFrom | null = null;
  if (power.consumes) {
    const handField = handAllocationField(item);
    const fromHand: ConsumedFrom | null = inHand > 0 ? handField : entry.held > 0 ? 'held' : entry.equipped > 0 ? 'equipped' : null;
    if (fighting) consumedFrom = fromHand ?? (hotlisted > 0 ? 'hotlist' : null);
    else consumedFrom = free > 0 ? 'free' : hotlisted > 0 ? 'hotlist' : fromHand;
    if (consumedFrom === null) return refuse(`There is no ${item.name} free to use.`);
  }

  const isPotion = item.kind === 'potion' && power.consumes;
  const spendsAction = fighting && (power.actionCost === 1 || isPotion);
  if (spendsAction) {
    const problem = encounterActionProblem(runtime, character.id, 1, 'normal');
    if (problem !== null) return refuse(problem);
  }

  return {
    canUse: true,
    refusal: null,
    spendsAction,
    consumedFrom,
    causesPotionSickness: isPotion && fighting && potionCadenceRemaining(runtime) > 0,
  };
}

/** Buffs started by Item Powers carry this prefix on their Effect id. */
export const ITEM_BUFF_PREFIX = 'power:';

export function isItemBuff(record: TimedEffectRecord): boolean {
  return record.effect.id.startsWith(ITEM_BUFF_PREFIX);
}

/**
 * One combat round passes for the Buffs Item Powers started, and ONLY those.
 *
 * WHY THIS EXISTS. At a shared table the GM's tracker completes rounds; a
 * crawler's own sheet never runs `advanceRound`. Without this, "Rallied for 3
 * rounds" would last until the next rest. This is called when the sheet applies
 * the GM's round-complete event, which is delivered exactly once per crawler.
 *
 * SCOPED ON PURPOSE. Printed Debuffs and Skill cooldowns keep whatever timing
 * they had before Item Powers existed; changing how THEY pass time is a rules
 * decision of its own and not one to make as a side effect.
 */
export function tickItemBuffs(runtime: RuntimeState, rounds = 1): { runtime: RuntimeState; ended: string[] } {
  if (!Number.isInteger(rounds) || rounds < 1 || !runtime.active.some(isItemBuff)) return { runtime, ended: [] };
  const ended: string[] = [];
  const active: TimedEffectRecord[] = [];
  for (const record of runtime.active) {
    if (!isItemBuff(record)) { active.push(record); continue; }
    let over = false;
    const endsWhen: ExpirationCondition[] = record.endsWhen.map((condition) => {
      if (condition.kind === 'after-rounds') {
        const left = condition.roundsRemaining - rounds;
        if (left <= 0) over = true;
        return { kind: 'after-rounds', roundsRemaining: Math.max(0, left) };
      }
      if (condition.kind === 'elapsed-time') {
        const left = condition.seconds - rounds * ROUND_SECONDS;
        if (left <= 0) over = true;
        return { kind: 'elapsed-time', seconds: Math.max(0, left) };
      }
      return condition;
    });
    if (over) ended.push(record.effect.label);
    else active.push({ ...record, endsWhen });
  }
  return { runtime: { ...runtime, active }, ended };
}

/** "3/3 charges · 1 back every 4h 00m · 10m 00s cooldown", for a list row. */
export function describeItemPowerRules(power: ItemPower): string {
  const parts: string[] = [];
  if (power.consumes) parts.push('used up');
  if (power.charges !== null) {
    parts.push(`${power.charges.max} charge${power.charges.max === 1 ? '' : 's'}`);
    const recharge = power.charges.recharge;
    parts.push(recharge === null
      ? 'does not recharge'
      : `${recharge.amount === 'all' ? 'all' : recharge.amount} back every ${describeElapsed(recharge.seconds)}`);
  }
  if (power.cooldownSeconds !== null) parts.push(`${describeElapsed(power.cooldownSeconds)} cooldown`);
  if (power.actionCost === 1) parts.push('1 Action in a fight');
  if (power.requiresEquipped) parts.push('must be worn or held');
  return parts.join(' · ');
}
